import { useEffect, useMemo, useRef, useState } from 'react';
import { Airplane, ArrowDownRight, ArrowUpRight, Crosshair, NavigationArrow, Siren, Star, Warning, X } from '@phosphor-icons/react';
import { useStore } from '../state/store';
import type { Aircraft, Airport, Enrichment, WatchEntry } from '../types';
import { findWatchEntry, isParked } from '../types';
import { altitudeColor, formatAltitude } from '../map/altitude';
import { categoryLabel } from '../map/planeIcon';
import { distanceKm } from '../map/geo';
import { ensureNotificationPermission } from '../alerts';
import { flightProgress, formatEta, formatEtaClock } from '../flightProgress';
import { apiUrl } from '../api';
import FlightHistory from './FlightHistory';
import { aircraftModel } from '../map/airframes';
import { airframePicture } from '../map/planeIcon';
import { useIsPhone } from '../hooks/useIsPhone';

const EMERGENCY_SQUAWKS: Record<string, string> = {
  '7500': 'Hijack',
  '7600': 'Radio failure',
  '7700': 'General emergency',
};

// Route data comes from adsbdb.com's callsign->route lookup — "what this
// callsign usually flies", not a per-flight schedule check — and airlines
// occasionally reuse a callsign for a different city pair on a given
// day/leg. Rather than silently trusting a route that contradicts what the
// aircraft is actually doing, cross-check against live position: if it's
// low, descending, and close to a real airport that ISN'T the reported
// destination, say so.
const APPROACH_MAX_ALT_FT = 3000;
const APPROACH_MIN_DESCENT_FPM = -300;
const APPROACH_MAX_DISTANCE_KM = 22; // ~12 nm

function nearestBigAirport(ac: Aircraft, airports: Airport[]): Airport | null {
  if (ac.onGround) return null;
  if (ac.altitudeFt == null || ac.altitudeFt > APPROACH_MAX_ALT_FT) return null;
  if (ac.verticalRateFpm == null || ac.verticalRateFpm > APPROACH_MIN_DESCENT_FPM) return null;
  let nearest: Airport | null = null;
  let nearestKm = APPROACH_MAX_DISTANCE_KM;
  for (const a of airports) {
    if (a.type === 'small_airport') continue;
    const km = distanceKm([ac.lon, ac.lat], [a.lon, a.lat]);
    if (km < nearestKm) {
      nearest = a;
      nearestKm = km;
    }
  }
  return nearest;
}

function formatAgo(seconds: number): string {
  if (seconds < 90) return `${seconds}s ago`;
  const m = Math.round(seconds / 60);
  return m < 90 ? `${m} min ago` : `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, '0')} min ago`;
}

export default function FlightInfoPanel() {
  const selected = useStore((s) => s.selectedIcao24);
  const aircraft = useStore((s) => (s.selectedIcao24 ? s.aircraft.get(s.selectedIcao24) : undefined));
  const airports = useStore((s) => s.airports);
  const select = useStore((s) => s.select);
  const setSelectedEnrichment = useStore((s) => s.setSelectedEnrichment);
  const followSelected = useStore((s) => s.followSelected);
  const setFollow = useStore((s) => s.setFollow);
  const watchlist = useStore((s) => s.watchlist);
  const addWatch = useStore((s) => s.addWatch);
  const removeWatch = useStore((s) => s.removeWatch);
  const focusTrack = useStore((s) => s.focusTrack);
  const [enrichment, setEnrichment] = useState<Enrichment | null>(null);
  const [loading, setLoading] = useState(false);
  const setSheetInset = useStore((s) => s.setSheetInset);
  const phone = useIsPhone();
  // Phone bottom sheet: opens as a short "peek" (who/where/how high) so the
  // plane and its track stay visible; swipe up or tap the handle for details.
  const [expanded, setExpanded] = useState(false);
  const panelRef = useRef<HTMLElement>(null);
  const swipeStartY = useRef<number | null>(null);
  // A swipe also ends in a click on the handle; this stops that click undoing it.
  const swiped = useRef(false);

  // Keep the last-known aircraft rendered for a moment after deselection so
  // the panel can animate out instead of popping away instantly.
  const [snapshot, setSnapshot] = useState(aircraft);

  useEffect(() => {
    if (aircraft) {
      setSnapshot(aircraft);
    } else if (snapshot) {
      const t = setTimeout(() => setSnapshot(undefined), 200);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aircraft]);

  const icao24 = aircraft?.icao24;
  const callsign = aircraft?.callsign;

  useEffect(() => setExpanded(false), [icao24]);

  // Tell the map how much of it the sheet covers, so the camera keeps the
  // selected plane in the visible strip above it.
  const sheetOpen = phone && !!aircraft;
  useEffect(() => {
    const el = panelRef.current;
    if (!sheetOpen || !el) {
      setSheetInset(0);
      return;
    }
    const ro = new ResizeObserver(() => setSheetInset(Math.round(el.getBoundingClientRect().height)));
    ro.observe(el);
    return () => {
      ro.disconnect();
      setSheetInset(0);
    };
  }, [sheetOpen, setSheetInset]);

  useEffect(() => {
    setEnrichment(null);
    setSelectedEnrichment(null);
    if (!icao24) return;
    let cancelled = false;
    setLoading(true);
    const q = callsign ? `?callsign=${encodeURIComponent(callsign)}` : '';
    fetch(apiUrl(`/api/enrichment/${icao24}${q}`))
      .then((r) => (r.ok ? r.json() : null))
      .then((d: Enrichment | null) => {
        if (!cancelled) {
          setEnrichment(d);
          setSelectedEnrichment(d);
        }
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    // Re-vet every minute while selected (quietly, no loading state): a route
    // that fit at cruise can stop fitting on descent, and finals get detected.
    const refresh = setInterval(() => {
      fetch(apiUrl(`/api/enrichment/${icao24}${q}`))
        .then((r) => (r.ok ? r.json() : null))
        .then((d: Enrichment | null) => {
          if (!cancelled && d) {
            setEnrichment(d);
            setSelectedEnrichment(d);
          }
        })
        .catch(() => undefined);
    }, 60_000);
    return () => {
      cancelled = true;
      clearInterval(refresh);
    };
  }, [icao24, callsign, setSelectedEnrichment]);

  const display = aircraft ?? snapshot;
  const nearAirport = useMemo(
    () => (display ? nearestBigAirport(display, airports) : null),
    [display, airports],
  );
  if (!display) return null;

  const closing = !selected || !aircraft;
  const emergency = display.squawk ? EMERGENCY_SQUAWKS[display.squawk] : undefined;
  const route = enrichment?.route;
  const info = enrichment?.aircraft;
  const photo = enrichment?.photo;
  const catLabel = categoryLabel(display.category);
  const seenAge = Math.max(0, Math.round((Date.now() - display.lastSeen) / 1000));
  // Flag when live position contradicts the reported (callsign-based, not
  // per-flight) destination — see nearestBigAirport's comment above.
  const routeMismatch =
    !loading && route && nearAirport && route.destinationIcao && route.destinationIcao !== nearAirport.ident;
  const inferredApproach = !loading && !route && nearAirport;
  // The published route is keyed by callsign, and crews often keep the
  // inbound flight number until the next leg is filed. The server knows
  // where *this* leg began; if the route ends there, it's the previous leg —
  // say so instead of showing it (and a bogus progress/ETA) as current.
  const legOrigin =
    focusTrack?.live && focusTrack.flight.icao24 === display.icao24 ? focusTrack.flight.origin : null;
  const routeIsPreviousLeg =
    !loading && !!route && !!legOrigin && route.destinationIcao === legOrigin && enrichment?.routeSource !== 'observed';
  // Say where the route comes from: a flight number's usual route is a guess
  // about today's flight; history and observation are what this plane did.
  const routeSourceText =
    enrichment?.routeSource === 'history'
      ? "From this flight number's recent flights out of the same airport"
      : enrichment?.routeSource === 'observed'
        ? route?.destinationIcao
          ? `Departure seen by PlaneSight · on final for ${route.destinationIcao}`
          : 'Departure seen by PlaneSight · destination not known yet'
        : enrichment?.routeSource === 'flight-number'
          ? 'Usual route for this flight number'
          : null;
  const parked = isParked(display);
  // Progress along the leg + ETA, computed from live position vs the route
  // endpoints (no schedule feed) — see flightProgress.ts for the caveats.
  const progress = routeIsPreviousLeg ? null : flightProgress(display, route ?? null);
  const watchEntry = findWatchEntry(display, watchlist);
  const watchKind: WatchEntry['kind'] = display.callsign ? 'callsign' : 'icao24';
  const watchValue = display.callsign?.trim() || display.icao24;
  const toggleWatch = () => {
    if (watchEntry) {
      removeWatch(watchEntry.id);
    } else {
      ensureNotificationPermission();
      addWatch(watchKind, watchValue);
    }
  };

  const onSwipeStart = (e: React.PointerEvent) => {
    swiped.current = false;
    if (phone) swipeStartY.current = e.clientY;
  };
  const onSwipeEnd = (e: React.PointerEvent) => {
    const start = swipeStartY.current;
    swipeStartY.current = null;
    if (start == null) return;
    const dy = e.clientY - start;
    if (Math.abs(dy) <= 30) return;
    swiped.current = true;
    if (dy < 0) setExpanded(true);
    else if (expanded) setExpanded(false);
    else select(null);
  };

  const vrate = display.verticalRateFpm;
  const vrateClass = vrate == null ? '' : vrate >= 200 ? 'is-climb' : vrate <= -200 ? 'is-descend' : '';
  // Make and model: our airframe table first (clean "Airbus A380-800" plus
  // real dimensions), else the airframe registry's text (hexdb.io).
  const model = aircraftModel(display.typeCode ?? info?.typeCode ?? null);
  const typeLabel =
    model?.label ??
    (info?.typeName || info?.typeCode
      ? `${info.manufacturer ? `${info.manufacturer} ` : ''}${info.typeName ?? info.typeCode}`
      : null);
  const typePicture = model ? airframePicture(model.key) : null;
  const typeMeta = model
    ? [
        model.kind,
        model.engines ? `${model.engines} engine${model.engines === 1 ? '' : 's'}` : null,
        `${model.lengthM} m long`,
        `${model.spanM} m wingspan`,
      ].filter((s): s is string => !!s)
    : [display.typeDesc ?? catLabel].filter((s): s is string => !!s);
  const subtitle = route?.airlineName ?? info?.operator ?? typeLabel ?? catLabel ?? 'Unknown operator';

  return (
    <aside
      ref={panelRef}
      className={`flight-panel side-panel glass${closing ? ' closing' : ''}${emergency ? ' is-emergency' : ''}${
        phone && !expanded ? ' sheet-peek' : ''
      }`}
      data-testid="flight-panel"
      aria-label={`Flight ${display.callsign ?? display.icao24.toUpperCase()}`}
    >
      {phone && (
        <button
          className="sheet-handle"
          onClick={() => {
            if (swiped.current) swiped.current = false;
            else setExpanded(!expanded);
          }}
          onPointerDown={onSwipeStart}
          onPointerUp={onSwipeEnd}
          onPointerCancel={() => (swipeStartY.current = null)}
          aria-expanded={expanded}
          aria-label={expanded ? 'Show less' : 'Show flight details'}
        />
      )}
      {/* Swipes start on the handle or header only, so scrolling the details never collapses the sheet. */}
      <header
        className="fp-head"
        onPointerDown={onSwipeStart}
        onPointerUp={onSwipeEnd}
        onPointerCancel={() => (swipeStartY.current = null)}
      >
        <span
          className="alt-swatch lg"
          style={{ background: altitudeColor(display.altitudeFt, display.onGround) }}
          aria-hidden="true"
        />
        <div className="fp-id">
          <h2 className="fp-callsign mono">{display.callsign ?? display.icao24.toUpperCase()}</h2>
          {loading && !enrichment ? (
            <span className="skeleton skeleton-line" style={{ width: '9em' }} />
          ) : (
            <span className="fp-sub">{subtitle}</span>
          )}
        </div>
        <div className="fp-actions">
          <button
            className={`icon-btn${watchEntry ? ' active' : ''}`}
            onClick={toggleWatch}
            title={watchEntry ? 'Remove from watchlist' : 'Add to watchlist'}
            aria-label={watchEntry ? 'Remove from watchlist' : 'Add to watchlist'}
            aria-pressed={!!watchEntry}
          >
            <Star size={17} weight={watchEntry ? 'fill' : 'bold'} />
          </button>
          <button
            className={`icon-btn${followSelected ? ' active' : ''}`}
            onClick={() => setFollow(!followSelected)}
            title={followSelected ? 'Stop following' : 'Keep the map centered on this aircraft'}
            aria-label={followSelected ? 'Stop following' : 'Follow this aircraft'}
            aria-pressed={followSelected}
          >
            <Crosshair size={17} weight="bold" />
          </button>
          <button className="icon-btn" onClick={() => select(null)} aria-label="Close" title="Close">
            <X size={17} weight="bold" />
          </button>
        </div>
      </header>

      {typeLabel && (
        <section className="fp-type" aria-label="Aircraft type">
          {typePicture && <img className="fp-type-pic" src={typePicture} alt="" aria-hidden="true" />}
          <div className="fp-type-text">
            <span className="fp-type-name">{typeLabel}</span>
            {typeMeta.length > 0 && (
              <span className="fp-type-meta">
                {typeMeta.map((item, i) => (
                  <span key={item} className="fp-type-item">
                    {i > 0 && ' · '}
                    {item}
                  </span>
                ))}
              </span>
            )}
          </div>
          {model && (
            <span className="fp-type-code mono" title="ICAO type designator">
              {model.code}
            </span>
          )}
        </section>
      )}

      {emergency && (
        <div className="emergency" role="alert">
          <Siren size={18} weight="fill" aria-hidden="true" />
          <span>
            <strong>{emergency}</strong>
            <span className="mono">Squawk {display.squawk}</span>
          </span>
        </div>
      )}

      {photo && (
        <figure className="fp-photo">
          <a href={photo.link || undefined} target="_blank" rel="noreferrer">
            <img
              src={photo.thumbnail}
              alt={`Photo of ${info?.registration ?? display.icao24.toUpperCase()}${info?.typeName ? `, a ${info.typeName}` : ''}`}
              loading="lazy"
            />
          </a>
          {photo.photographer && <figcaption>Photo: {photo.photographer} / planespotters.net</figcaption>}
        </figure>
      )}
      {loading && !photo && <div className="skeleton fp-photo-skeleton" aria-hidden="true" />}

      <section className="fp-route" aria-label="Route">
        {loading ? (
          <div className="fp-route-strip" aria-busy="true">
            <div className="fp-apt">
              <span className="skeleton skeleton-code" />
              <span className="skeleton skeleton-line" style={{ width: '5em' }} />
            </div>
            <div className="fp-route-line" />
            <div className="fp-apt end">
              <span className="skeleton skeleton-code" />
              <span className="skeleton skeleton-line" style={{ width: '5em' }} />
            </div>
          </div>
        ) : route ? (
          <>
            <div className={`fp-route-strip${routeIsPreviousLeg ? ' is-previous' : ''}`}>
              <div className="fp-apt">
                <span className="fp-apt-code mono">{route.originIcao ?? '----'}</span>
                <span className="fp-apt-city">{route.originMunicipality ?? route.originName ?? (route.originIcao ? '' : 'Not known')}</span>
              </div>
              <div
                className="fp-route-line"
                role={progress ? 'progressbar' : undefined}
                aria-valuemin={progress ? 0 : undefined}
                aria-valuemax={progress ? 100 : undefined}
                aria-valuenow={progress ? Math.round(progress.fraction * 100) : undefined}
                aria-label={progress ? 'Flight progress' : undefined}
              >
                {progress && (
                  <>
                    <span className="fp-route-fill" style={{ transform: `scaleX(${progress.fraction})` }} />
                    <span className="fp-route-plane" style={{ left: `${progress.fraction * 100}%` }}>
                      <Airplane size={14} weight="fill" />
                    </span>
                  </>
                )}
              </div>
              <div className="fp-apt end">
                <span className="fp-apt-code mono">{route.destinationIcao ?? '----'}</span>
                <span className="fp-apt-city">
                  {route.destinationMunicipality ?? route.destinationName ?? (route.destinationIcao ? '' : 'Not known yet')}
                </span>
              </div>
            </div>
            {progress && (
              <div className="fp-progress">
                <span className="mono">{Math.round(progress.distanceFlownKm).toLocaleString()} km flown</span>
                {progress.etaMs != null ? (
                  <span className="fp-eta">
                    <span className="mono">ETA {formatEtaClock(progress.etaMs)}</span>
                    {formatEta(progress.etaMs) && <span className="fp-eta-left">{formatEta(progress.etaMs)} left</span>}
                  </span>
                ) : (
                  <span className="mono">{Math.round(progress.distanceRemainingKm).toLocaleString()} km to go</span>
                )}
              </div>
            )}
            {routeSourceText && <p className="fp-route-source">{routeSourceText}</p>}
          </>
        ) : (
          <p className="fp-muted">
            {inferredApproach && nearAirport
              ? `No published route. Approaching ${nearAirport.ident} (${nearAirport.municipality ?? nearAirport.name}).`
              : 'No published route for this callsign.'}
          </p>
        )}
        {enrichment?.routeNote && (
          <p className="route-caveat" role="note">
            <Warning size={15} weight="fill" aria-hidden="true" />
            <span>{enrichment.routeNote}</span>
          </p>
        )}
        {routeIsPreviousLeg && route && (
          <p className="route-caveat" role="note">
            <Warning size={15} weight="fill" aria-hidden="true" />
            <span>
              That's the previous flight under this callsign, into {legOrigin}. This aircraft{' '}
              {display.onGround ? 'is at' : 'departed'} {legOrigin}; its new route shows once the airline updates the
              flight number.
            </span>
          </p>
        )}
        {routeMismatch && !routeIsPreviousLeg && enrichment?.routeSource === 'flight-number' && nearAirport && (
          <p
            className="route-caveat"
            title="Route data comes from the flight's callsign, not this specific flight's schedule, so it can be outdated."
          >
            <Warning size={15} weight="fill" aria-hidden="true" />
            <span>
              Now approaching {nearAirport.ident} ({nearAirport.municipality ?? nearAirport.name}). The listed
              destination may be outdated.
            </span>
          </p>
        )}
      </section>

      <div className="readouts">
        <div className="readout">
          <span className="readout-label">Altitude</span>
          <span className="readout-value mono">{formatAltitude(display.altitudeFt, display.onGround)}</span>
        </div>
        <div className="readout">
          <span className="readout-label">Ground speed</span>
          <span className="readout-value mono">
            {display.groundSpeedKt != null ? (
              <>
                {display.groundSpeedKt}
                <span className="readout-unit">kt</span>
              </>
            ) : (
              '---'
            )}
          </span>
        </div>
        <div className={`readout ${vrateClass}`}>
          <span className="readout-label">Vertical rate</span>
          <span className="readout-value mono">
            {vrate == null ? (
              '---'
            ) : (
              <>
                {vrate >= 200 ? (
                  <ArrowUpRight size={15} weight="bold" aria-label="Climbing" />
                ) : vrate <= -200 ? (
                  <ArrowDownRight size={15} weight="bold" aria-label="Descending" />
                ) : null}
                {vrate > 0 ? `+${vrate}` : vrate}
                <span className="readout-unit">fpm</span>
              </>
            )}
          </span>
        </div>
        <div className="readout">
          <span className="readout-label">Track</span>
          <span className="readout-value mono">
            {display.track != null ? (
              <>
                <span
                  className="track-arrow"
                  style={{ transform: `rotate(${Math.round(display.track) + 45}deg)` }}
                  aria-hidden="true"
                >
                  <NavigationArrow size={13} weight="fill" />
                </span>
                {String(Math.round(display.track) % 360).padStart(3, '0')}°
              </>
            ) : (
              '---'
            )}
          </span>
        </div>
      </div>

      <dl className="facts">
        {(info?.registration ?? display.registration) && (
          <div>
            <dt>Registration</dt>
            <dd className="mono">{info?.registration ?? display.registration}</dd>
          </div>
        )}
        {typeLabel && (
          <div>
            <dt>Aircraft</dt>
            <dd>{typeLabel}</dd>
          </div>
        )}
        {info?.operator && (
          <div>
            <dt>Operator</dt>
            <dd>{info.operator}</dd>
          </div>
        )}
        {catLabel && (
          <div>
            <dt>Category</dt>
            <dd>{catLabel}</dd>
          </div>
        )}
        <div>
          <dt>Squawk</dt>
          <dd className={`mono${emergency ? ' squawk-emergency' : ''}`}>{display.squawk ?? '----'}</dd>
        </div>
        <div>
          <dt>ICAO24</dt>
          <dd className="mono">{display.icao24.toUpperCase()}</dd>
        </div>
        <div>
          <dt>Sources</dt>
          <dd>{display.sources.join(', ')}</dd>
        </div>
        <div>
          <dt>{parked ? 'Parked · last seen' : 'Last seen'}</dt>
          <dd className="mono">{formatAgo(seenAge)}</dd>
        </div>
      </dl>

      {!closing && <FlightHistory icao24={display.icao24} flightId={display.flightId ?? null} />}
    </aside>
  );
}
